import React, { ChangeEvent, useState, useCallback, useEffect } from 'react';
import { get, debounce } from 'lodash';
import { Field, useFormikContext } from 'formik';
import {
  AuErrorText,
  AuInputLabel,
  AuHintText,
  AuFormItemWrapper
} from 'components/DTA';
import { IAddressResult, IGetAddressRequest, IGetAddressResponse } from 'services/arnService/arnServiceTypes';
import useArnService from 'services/arnService/useArnService';

import './ExperianAddressLookup.scss';

interface IAddressLookup {
  name: string;
  label?: string;
  className?: string;
  hintText?: string;
  disabled?: boolean;
  dark?: boolean;
  block?: boolean;
  style?: React.CSSProperties;
  callback?(e: any): void;
}

const ExperianAddressLookup: React.FC<IAddressLookup> = ({ name, callback, label, className = '', block, disabled = false, dark = false, hintText }) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState<IAddressResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [input, setInput] = useState('');
  const [cursor, setCursor] = useState(0);

  const { errors, getFieldMeta, setFieldValue } = useFormikContext();

  const fieldError = get(errors, name);
  const arnService = useArnService();

  useEffect(() => {
    const currentValue = getFieldMeta<string>(name).value;
    if (currentValue) {
      setInput(currentValue);
    }
  }, []);

  const getAddressSuggestions = async (request: IGetAddressRequest): Promise<IGetAddressResponse | null> => {
    try {
      const response = await arnService.getAddress(request);
      return (response && response.wasSuccessful) ? response : null;
    }
    catch (error) {
      return null;
    }
  };

  const getAddressComponentDetails = async (addressId: string | undefined) => {
    if (addressId) {
      try {
        const addressComponentsResponse = await arnService.getAddressComponentDetails(addressId);
        if (addressComponentsResponse.wasSuccessful && callback) {
          callback(addressComponentsResponse.components);
        }
      }
      catch (error) {
        return;
      }
    }
  };

  const handleArrowKey = (e: React.KeyboardEvent<HTMLElement>) => {
    const curCursorValue = cursor;

    if (e.key === 'Enter' && filteredSuggestions.length > 0 && curCursorValue >= 0) {
      e.preventDefault();
      const selectedAddress = filteredSuggestions[curCursorValue];
      const inputText = selectedAddress.suggestion;
      setFilteredSuggestions([]);
      setInput(inputText);
      setShowSuggestions(false);
      // set the formik field value
      setFieldValue(name, inputText);
      if (callback) {
        getAddressComponentDetails(selectedAddress?.id);
      }
    }
    if (e.key === 'ArrowUp' && cursor > 0) {
      setCursor(curCursorValue - 1);
    }
    else if (e.key === 'ArrowDown') {
      if (cursor < filteredSuggestions.length - 1) {
        setCursor(curCursorValue + 1);
      }
      else if (cursor == filteredSuggestions.length - 1) {
        setCursor(0);
      }
    }
  };

  const handleOnMouseClick = (e: React.MouseEvent<HTMLElement>) => {
    setFilteredSuggestions([]);
    const input = e.target as HTMLElement;
    setInput(input.innerText);
    setShowSuggestions(false);
    // set the formik field value
    setFieldValue(name, input.innerText);
    if (callback) {
      const selectedAddress = filteredSuggestions.find(address => address.suggestion == input.innerText);
      getAddressComponentDetails(selectedAddress?.id);
    }
  };

  const handleDebounceCallback = async (inputValue) => {
    const request: IGetAddressRequest = {
      address: inputValue
    };

    const response = await getAddressSuggestions(request);
    if (response && response.searchAddressResponse.results.length > 0) {
      const suggestions = response.searchAddressResponse.results.map(address => {
        return { ...address, suggestion: address.suggestion.replace(/\s\s/g, ' ') }; //Remove extra consecutive white spaces from suggesstion
      });
      setFilteredSuggestions(suggestions);
      setShowSuggestions(true);
    }
  };

  const debounceCallback = useCallback(debounce(handleDebounceCallback, 400), []);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
    setFieldValue(name, event.target.value);
    debounceCallback(event.target.value);
  };

  return (
    <AuFormItemWrapper>
      {
        label && <AuInputLabel text={label} htmlFor={name} />
      }
      {
        hintText && <AuHintText text={hintText} htmlFor={name} />
      }
      <Field
        name={name}
      >
        {() => (
          <>
            <input
              type='text'
              onChange={handleOnChange}
              onBlur={() => setShowSuggestions(false)}
              value={input}
              disabled={disabled}
              onKeyDown={handleArrowKey}
              className={
                `au-text-input ${className} ` +
                `${dark ? ' au-text-input--dark' : ''}` +
                `${block ? ' au-text-input--block' : ''}` +
                `${getFieldMeta(name).error && !disabled ? ' au-text-input--invalid' : ''}`
              } />
            {showSuggestions &&
              input &&
              <div className='suggestions-wrapper'>
                <ul className='suggestions'>
                  {filteredSuggestions.map((addressItem, index) =>
                    <li className={cursor === index ? 'suggestion-active' : ''} key={addressItem.id} onMouseDown={handleOnMouseClick} >
                      {addressItem.suggestion}
                    </li>
                  )}
                </ul>
              </div>
            }
            {fieldError && !disabled &&
              <AuErrorText text={get(errors, name)} />
            }
          </>
        )}
      </Field>
    </AuFormItemWrapper>
  );
};

export default ExperianAddressLookup;
